import React, { useEffect, useRef } from "react";
import {
  X,
  Search,
  Megaphone,
  Calendar,
  Building2,
  MapPin,
  ShoppingCart,
  Utensils,
  Search as SearchIcon,
  ShoppingBag,
  Wrench,
  ArrowRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { cn } from "../../lib/utils";
import { useLocationContext } from "../../client/contexts/LocationContext";
import type { GlobalSearchResult } from "../../gedoba/search/queries";

interface SearchResultsModalProps {
  isOpen: boolean;
  onClose: () => void;
  searchTerm: string;
  results: GlobalSearchResult[];
  isLoading: boolean;
  total: number;
  byType: {
    notices: number;
    events: number;
    tenants: number;
    facilities: number;
    buySell: number;
    foodCourt: number;
    lostFound: number;
    retail: number;
    services: number;
  };
}

const typeConfig: Record<
  string,
  { icon: typeof Search; label: string; color: string }
> = {
  NOTICE: { icon: Megaphone, label: "Notice", color: "text-purple-600" },
  EVENT: { icon: Calendar, label: "Event", color: "text-blue-500" },
  TENANT: { icon: Building2, label: "Tenant", color: "text-cyan-600" },
  FACILITY: { icon: MapPin, label: "Facility", color: "text-teal-600" },
  BUY_SELL: {
    icon: ShoppingCart,
    label: "Buy & Sell",
    color: "text-green-600",
  },
  FOOD_COURT: { icon: Utensils, label: "Food Court", color: "text-orange-600" },
  LOST_FOUND: {
    icon: SearchIcon,
    label: "Lost & Found",
    color: "text-blue-500",
  },
  RETAIL: { icon: ShoppingBag, label: "Retail", color: "text-pink-600" },
  SERVICE: { icon: Wrench, label: "Service", color: "text-purple-500" },
};

export default function SearchResultsModal({
  isOpen,
  onClose,
  searchTerm,
  results,
  isLoading,
  total,
  byType,
}: SearchResultsModalProps) {
  const navigate = useNavigate();
  const { selectedLocation } = useLocationContext();
  const modalRef = useRef<HTMLDivElement>(null);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleResultClick = (result: GlobalSearchResult) => {
    if (selectedLocation?.locationCode) {
      navigate(`/locations/${selectedLocation.locationCode}${result.url}`);
    } else {
      navigate(result.url);
    }
    onClose();
  };

  // Group results by type
  const groupedResults = results.reduce(
    (acc, result) => {
      if (!acc[result.type]) {
        acc[result.type] = [];
      }
      acc[result.type].push(result);
      return acc;
    },
    {} as Record<string, GlobalSearchResult[]>,
  );

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 px-4">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        ref={modalRef}
        className="relative w-full max-w-3xl max-h-[80vh] bg-content-bg border border-border rounded-lg shadow-xl overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-3">
            <Search className="w-5 h-5 text-header-text-muted" />
            <div>
              <h2 className="text-lg font-semibold text-header-text">
                Search Results
                {searchTerm && (
                  <span className="text-sm font-normal text-header-text-muted ml-2">
                    for "{searchTerm}"
                  </span>
                )}
              </h2>
              {total > 0 && (
                <p className="text-sm text-header-text-muted">
                  {total} result{total !== 1 ? "s" : ""} found
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-sidebar-hover rounded-lg transition-colors"
            title="Close"
          >
            <X className="w-5 h-5 text-header-text-muted" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
                <p className="text-content-text-muted">Searching...</p>
              </div>
            </div>
          ) : total === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-4">
              <Search className="w-12 h-12 text-content-text-muted mb-4" />
              <p className="text-lg font-medium text-content-text mb-2">
                No results found
              </p>
              <p className="text-sm text-content-text-muted text-center">
                Try searching with different keywords or check your spelling.
              </p>
            </div>
          ) : (
            <div className="p-4 space-y-6">
              {/* Results by Type */}
              {Object.entries(groupedResults).map(([type, typeResults]) => {
                const config = typeConfig[type];
                const Icon = config?.icon || Search;
                const count =
                  byType[type.toLowerCase() as keyof typeof byType] || 0;

                return (
                  <div key={type} className="space-y-2">
                    <div className="flex items-center gap-2 pb-2 border-b border-border">
                      <Icon
                        className={cn(
                          "w-4 h-4",
                          config?.color || "text-content-text-muted",
                        )}
                      />
                      <h3 className="font-semibold text-content-text">
                        {config?.label || type} ({count})
                      </h3>
                    </div>
                    <div className="space-y-1">
                      {typeResults.map((result) => (
                        <button
                          key={result.id}
                          onClick={() => handleResultClick(result)}
                          className="w-full text-left p-3 rounded-lg hover:bg-sidebar-hover transition-colors group"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-content-text group-hover:text-primary transition-colors line-clamp-1">
                                {result.title}
                              </h4>
                              {result.description && (
                                <p className="text-sm text-content-text-muted mt-1 line-clamp-2">
                                  {result.description}
                                </p>
                              )}
                            </div>
                            <ArrowRight className="w-4 h-4 text-content-text-muted group-hover:text-primary transition-colors flex-shrink-0 mt-1" />
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer with type summary */}
        {total > 0 && (
          <div className="p-4 border-t border-border bg-sidebar-bg">
            <div className="flex flex-wrap gap-2 text-xs">
              {Object.entries(byType).map(([type, count]) => {
                if (count === 0) return null;
                const config =
                  typeConfig[type.toUpperCase().replace("BUYSELL", "BUY_SELL")];
                return (
                  <div
                    key={type}
                    className="flex items-center gap-1 px-2 py-1 rounded bg-content-bg border border-border"
                  >
                    {config && (
                      <config.icon className={cn("w-3 h-3", config.color)} />
                    )}
                    <span className="text-content-text-muted">
                      {config?.label || type}: {count}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
